/**
 * Kingdom of Beauty — Client Enhancement Scripts
 * Hero Ken Burns, scroll reveal, GSAP image parallax, pinned milestone timeline
 */
(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;
  const isMobileViewport = window.matchMedia('(max-width: 767px)').matches;

  function whenGsapReady(cb) {
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      cb();
      return;
    }
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        clearInterval(timer);
        cb();
      } else if (tries > 120) {
        clearInterval(timer);
        cb();
      }
    }, 50);
  }

  function runAfterLcp(fn) {
    if (!isMobileViewport) {
      fn();
      return;
    }
    const start = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(fn, { timeout: 3000 });
      } else {
        setTimeout(fn, 400);
      }
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  /* ----------------------------------------------------------
   * Preloader
   * ---------------------------------------------------------- */
  function initBrandedPreloader() {
    const preload = document.getElementById('kob-preload-screen');
    if (!preload) return;

    document.body.classList.add('kob-preload-active');

    const hidePreloader = () => {
      document.body.classList.remove('kob-preload-active');
      document
        .querySelectorAll('.slideshow')
        .forEach((s) => s.classList.add('kob-hero-ready'));

      const removePreload = () => preload.remove();

      if (typeof motion !== 'undefined' && motion.animate) {
        const animation = motion.animate(
          preload,
          { opacity: [1, 0], visibility: ['visible', 'hidden'] },
          { ease: [0.22, 1, 0.36, 1], duration: 0.85 }
        );

        if (animation && typeof animation.then === 'function') {
          animation.then(removePreload).catch(removePreload);
        } else if (animation?.finished?.then) {
          animation.finished.then(removePreload).catch(removePreload);
        } else {
          removePreload();
        }
      } else {
        preload.style.transition = 'opacity 0.85s ease, visibility 0.85s ease';
        preload.style.opacity = '0';
        preload.style.visibility = 'hidden';
        setTimeout(removePreload, 900);
      }
    };

    const minDisplay = 1200;
    const start = Date.now();

    const onReady = () => {
      const elapsed = Date.now() - start;
      const delay = Math.max(0, minDisplay - elapsed);
      setTimeout(hidePreloader, delay);
    };

    if (document.readyState === 'complete') {
      onReady();
    } else {
      window.addEventListener('load', onReady, { once: true });
    }
  }

  /* ----------------------------------------------------------
   * Hero — Ken Burns (GSAP, CSS fallback)
   * ---------------------------------------------------------- */
  function initHeroKenBurns(root = document) {
    if (prefersReducedMotion) return;

    const mediaItems = root.querySelectorAll?.('.kob-ken-burns-media') || [];
    mediaItems.forEach((media) => {
      if (media.dataset.kobKenBurnsInit === 'true') return;

      const target =
        media.querySelector('picture img') || media.querySelector(':scope > img');
      if (!target) return;

      media.dataset.kobKenBurnsInit = 'true';
      target.classList.add('kob-ken-burns-target');

      /* Mobile: CSS-only Ken Burns — avoids GSAP competing with LCP */
      if (isMobileViewport || typeof gsap === 'undefined') {
        target.classList.add('kob-ken-burns-fallback');
        return;
      }

      gsap.killTweensOf(target);
      gsap.set(target, {
        scale: 1,
        xPercent: 0,
        yPercent: 0,
        transformOrigin: '50% 50%',
      });
      gsap.to(target, {
        scale: 1.14,
        xPercent: -1.8,
        yPercent: -1.2,
        duration: 18,
        ease: 'sine.inOut',
        repeat: -1,
        yoyo: true,
      });
    });
  }

  /* ----------------------------------------------------------
   * Hero heading — visible immediately
   * ---------------------------------------------------------- */
  function initHeroWordReveal() {
    const headings = document.querySelectorAll(
      '.slideshow .kob-hero-heading[data-hero-word-reveal="true"], .slideshow .kob-hero-heading[data-hero-mask-reveal="true"]'
    );
    if (!headings.length) return;

    headings.forEach((heading) => {
      if (heading.dataset.kobMaskRevealInit === 'true') return;
      heading.dataset.kobMaskRevealInit = 'true';
      heading.classList.add('kob-mask-ready');
    });
  }

  /* ----------------------------------------------------------
   * Scroll fade-up + parallax
   * ---------------------------------------------------------- */
  function initScrollReveal() {
    if (prefersReducedMotion) return;

    const main = document.getElementById('MainContent');
    if (!main) return;

    const skipReveal = [
      '.slideshow',
      '[class*="luxury-ambient-bg-"]',
      '.kob-milestone-section',
      '.price-list-hero',
      '.price-list-section',
      '.stacked-images',
      '.section-price-list-hero',
      '.section-price-list',
      '.section-stacked-images',
    ].join(',');

    const sections = main.querySelectorAll(':scope > .shopify-section');
    sections.forEach((section, index) => {
      if (section.querySelector(skipReveal)) return;
      if (section.matches(skipReveal)) return;
      section.classList.add('kob-reveal-section');
      section.style.transitionDelay = `${Math.min(index * 0.04, 0.4)}s`;
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    sections.forEach((section) => {
      if (!section.classList.contains('kob-reveal-section')) return;

      const rect = section.getBoundingClientRect();
      const inView =
        rect.top < window.innerHeight * 0.95 && rect.bottom > 40;

      /* Keep above-the-fold content visible — avoid flash then hide */
      if (inView) {
        section.classList.add('is-visible');
        return;
      }

      observer.observe(section);
    });
  }

  function initParallax(root) {
    if (!root || prefersReducedMotion) return;

    const excludedMedia = [
      '.product-item',
      '.product-grid',
      '.product-card',
      '.product-item__wrapper',
      '.product-item__inner',
      '.product-item__media',
      '.product__media',
      '.product__promotion-banner',
      '.product-banner__image',
      '.product-banner__image-wrapper',
      '.product-detail__media',
      '.product-single__media',
      '.main-product',
      '.main-product-section',
      '.sec__collection-main',
      '.sec__product-banner',
      '.sec__featured-product',
      '.cart-item',
      '.minicart',
      '.search_type_drawer',
      '.search-popup',
      '.video-item__product-media--ratio',
      '.shopable-video__product',
      '.bundle-image',
      '.horizontal-product__media',
      '.block-text-product__infor-image',
      '.media-gallery__image',
      'media-gallery',
      'product-form',
      'product-form-bundle',
      '.stacked-images',
      '.section-stacked-images',
      '.price-list-hero',
      '.section-price-list-hero',
      '.price-list-section',
      '.section-price-list',
    ].join(',');

    const imageSelectors = '.shopify-section img';
    const sectionMediaContainers = [
      '.banner__media',
      '.background_image',
      '.newsletter-popup__banner',
      '.masonry-grid-3__item',
      '.collection-item__media',
      '.blog-posts-image',
      '.instagram-item__media--ratio',
      '.lookbook-item__media--ratio',
      '.lookbook-product__media',
      '.venue-media',
      '.venue-card',
      '.slide',
      '.testimonials-author-image',
      '.section_tiktok-image',
      '.video-image',
      '.image-box',
      '.service-image',
      '.service-card',
      '.story-image',
      '.story-media',
      '.sec__iwto .sec__inner',
    ].join(',');

    const isSmallDecorativeImage = (img) => {
      const width = img.offsetWidth || img.naturalWidth || 0;
      const height = img.offsetHeight || img.naturalHeight || 0;
      return width > 0 && height > 0 && (width < 140 || height < 140);
    };

    const containers = new Map();
    root.querySelectorAll(imageSelectors).forEach((img) => {
      if (img.closest(excludedMedia)) return;
      if (img.closest('.kob-ken-burns-media')) return;
      if (img.closest('.sec__instagram')) return;
      if (img.closest('header, footer, .header, .footer')) return;
      if (img.classList.contains('product-image')) return;
      if (isSmallDecorativeImage(img)) return;

      const container =
        img.closest(sectionMediaContainers) ||
        img.closest('a, figure, .media, .image, .responsive-image') ||
        img.parentElement;
      if (!container || containers.has(container)) return;
      if (container.closest(excludedMedia)) return;
      if (container.closest('header, footer, .header, .footer')) return;

      containers.set(container, img);
    });

    containers.forEach((img, container) => {
      if (img.dataset.kobParallaxInit === 'true') return;
      img.dataset.kobParallaxInit = 'true';
      container.classList.add('kob-parallax-wrap');
      img.classList.add('kob-parallax-media');

      const isInstagramImage = container.matches('.instagram-item__media--ratio');
      const parallaxDistance = isInstagramImage ? 11 : 7;
      const parallaxScale = isInstagramImage ? 1.14 : 1.08;

      if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        gsap.registerPlugin(ScrollTrigger);
        gsap.fromTo(
          img,
          { yPercent: -parallaxDistance, scale: parallaxScale },
          {
            yPercent: parallaxDistance,
            scale: parallaxScale,
            ease: 'none',
            scrollTrigger: {
              trigger: container,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true,
            },
          }
        );
        return;
      }

      const onScroll = () => {
        const rect = container.getBoundingClientRect();
        const viewH = window.innerHeight;
        if (rect.bottom < 0 || rect.top > viewH) return;

        const progress = (viewH - rect.top) / (viewH + rect.height);
        const shift = (progress - 0.5) * parallaxDistance * 2;
        img.style.setProperty('--kob-parallax-y', `${shift}%`);
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      onScroll();
    });
  }

  function initInstagramParallax(root = document) {
    if (prefersReducedMotion) return;

    const scope = root.matches?.('.sec__instagram')
      ? root
      : root.querySelector?.('.sec__instagram');
    if (!scope) return;

    const tiles = scope.querySelectorAll('.instagram-item__media--ratio');
    if (!tiles.length) return;

    const setupTile = (tile) => {
      if (tile.dataset.kobInstagramParallaxInit === 'true') return;

      const target = tile.querySelector('picture') || tile.querySelector('img');
      if (!target) return;

      tile.dataset.kobInstagramParallaxInit = 'true';
      tile.classList.add('kob-instagram-parallax-wrap');
      target.classList.add('kob-instagram-parallax-target');

      if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        gsap.registerPlugin(ScrollTrigger);
        gsap.fromTo(
          target,
          { yPercent: -10, scale: 1.12 },
          {
            yPercent: 10,
            scale: 1.12,
            ease: 'none',
            scrollTrigger: {
              trigger: tile,
              start: 'top bottom',
              end: 'bottom top',
              scrub: 0.6,
              invalidateOnRefresh: true,
            },
          }
        );
        return;
      }

      const onScroll = () => {
        const rect = tile.getBoundingClientRect();
        const viewH = window.innerHeight;
        if (rect.bottom < 0 || rect.top > viewH) return;

        const progress = (viewH - rect.top) / (viewH + rect.height);
        const shift = (progress - 0.5) * 20;
        target.style.setProperty('--kob-instagram-parallax-y', `${shift}%`);
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      onScroll();
    };

    tiles.forEach((tile) => {
      const img = tile.querySelector('img');
      if (img && !img.complete) {
        img.addEventListener('load', () => setupTile(tile), { once: true });
      }
      setupTile(tile);
    });
  }

  /* ----------------------------------------------------------
   * Milestone — GSAP ScrollTrigger pin + horizontal scroll
   * ---------------------------------------------------------- */
  function initMilestoneTimeline() {
    const sections = document.querySelectorAll('.kob-milestone-section');
    if (!sections.length) return;

    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      sections.forEach(fallbackMilestoneScroll);
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    const usePinTimeline = window.matchMedia('(min-width: 768px)').matches;

    sections.forEach((section) => {
      if (section.dataset.kobMilestoneInit === 'true') return;
      section.dataset.kobMilestoneInit = 'true';

      if (!usePinTimeline) {
        fallbackMilestoneScroll(section);
        return;
      }

      const viewport = section.querySelector('.timeline-viewport');
      const track = section.querySelector('.timeline-track');
      const items = section.querySelectorAll('.timeline-item');
      const progressBar = section.querySelector('.kob-milestone-progress__bar');
      const prevBtn = section.querySelector('[data-timeline-prev]');
      const nextBtn = section.querySelector('[data-timeline-next]');

      if (!viewport || !track || !items.length) return;

      const getScrollAmount = () =>
        Math.max(0, track.scrollWidth - viewport.clientWidth);

      const tween = gsap.to(track, {
        x: () => -getScrollAmount(),
        ease: 'none',
        scrollTrigger: {
          trigger: section,
          pin: true,
          scrub: 1,
          start: 'top 50px',
          end: () => `+=${getScrollAmount() + window.innerHeight * 0.35}`,
          invalidateOnRefresh: true,
          anticipatePin: 1,
          onUpdate: (self) => {
            if (progressBar) {
              progressBar.style.width = `${self.progress * 100}%`;
            }
            updateActiveItem(items, self.progress);
          },
        },
      });

      items.forEach((item) => {
        const yearEl = item.querySelector('.year-text');
        const yearTarget = parseInt(yearEl?.dataset.year || yearEl?.textContent, 10);
        if (yearEl && !isNaN(yearTarget)) {
          item._yearTarget = yearTarget;
          item._yearEl = yearEl;
        }
      });

      if (prevBtn && nextBtn) {
        prevBtn.addEventListener('click', () => {
          const st = tween.scrollTrigger;
          if (!st) return;
          st.scroll(st.start + (st.end - st.start) * Math.max(0, st.progress - 0.2));
        });
        nextBtn.addEventListener('click', () => {
          const st = tween.scrollTrigger;
          if (!st) return;
          st.scroll(st.start + (st.end - st.start) * Math.min(1, st.progress + 0.2));
        });
      }

      ScrollTrigger.addEventListener('refreshInit', () => {
        gsap.set(track, { x: 0 });
      });
    });

    ScrollTrigger.refresh();
  }

  function updateActiveItem(items, progress) {
    const activeIndex = Math.round(progress * (items.length - 1));
    items.forEach((item, i) => {
      item.classList.toggle('is-active', i <= activeIndex);
    });
  }

  function fallbackMilestoneScroll(section) {
    const viewport = section.querySelector('.timeline-viewport');
    const prevBtn = section.querySelector('[data-timeline-prev]');
    const nextBtn = section.querySelector('[data-timeline-next]');
    if (!viewport) return;

    viewport.style.overflowX = 'auto';

    const updateButtons = () => {
      if (!prevBtn || !nextBtn) return;
      prevBtn.disabled = viewport.scrollLeft <= 5;
      nextBtn.disabled =
        viewport.scrollLeft >= viewport.scrollWidth - viewport.clientWidth - 5;
    };

    prevBtn?.addEventListener('click', () => {
      viewport.scrollBy({ left: -400, behavior: 'smooth' });
    });
    nextBtn?.addEventListener('click', () => {
      viewport.scrollBy({ left: 400, behavior: 'smooth' });
    });

    viewport.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons);
    updateButtons();
  }

  /* ----------------------------------------------------------
   * Instagram gallery
   * ---------------------------------------------------------- */
  function initInstagramGallery() {
    const section = document.querySelector('.sec__instagram');
    if (!section) return;

    const items = section.querySelectorAll('.grid-custom-item, .swiper-slide');
    if (!items.length) return;

    const staggerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Array.from(items).indexOf(entry.target);
            setTimeout(() => {
              entry.target.classList.add('kob-instagram-visible');
            }, index * 80);
            staggerObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );

    items.forEach((item) => {
      item.classList.remove('scroll-trigger');
      staggerObserver.observe(item);
      initLightboxTrigger(item);
    });

    ensureLightboxMarkup();
  }

  function ensureLightboxMarkup() {
    if (document.getElementById('kob-lightbox')) return;

    const lightbox = document.createElement('div');
    lightbox.id = 'kob-lightbox';
    lightbox.className = 'kob-lightbox';
    lightbox.innerHTML = `
      <button type="button" class="kob-lightbox__close" aria-label="Close">&times;</button>
      <img class="kob-lightbox__img" src="" alt="">
    `;
    document.body.appendChild(lightbox);

    const close = () => lightbox.classList.remove('is-open');
    lightbox.querySelector('.kob-lightbox__close').addEventListener('click', close);
    lightbox.addEventListener('click', (e) => {
      if (e.target === lightbox) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });
  }

  function initLightboxTrigger(item) {
    const media = item.querySelector(
      '.instagram-item__media--ratio img, .slide img'
    );
    if (!media) return;

    media.style.cursor = 'zoom-in';
    item.addEventListener('click', (e) => {
      const link = item.querySelector('a[href]');
      if (link && link.getAttribute('href') && link.getAttribute('href') !== '#') {
        return;
      }

      e.preventDefault();
      const lightbox = document.getElementById('kob-lightbox');
      const img = lightbox?.querySelector('.kob-lightbox__img');
      if (!lightbox || !img) return;

      img.src =
        media.currentSrc || media.src.replace(/width=\d+/, 'width=2000');
      img.alt = media.alt || 'Gallery image';
      lightbox.classList.add('is-open');
    });
  }

  function initSalonSliderCards() {
    document.querySelectorAll('.salon-slider .slide').forEach((slide) => {
      if (slide.classList.contains('kob-service-card')) return;

      const img = slide.querySelector('img');
      if (!img) return;

      slide.classList.add('kob-service-card');
      const media = document.createElement('div');
      media.className = 'kob-service-card__media';
      img.parentNode.insertBefore(media, img);
      media.appendChild(img);

      const overlay = document.createElement('div');
      overlay.className = 'kob-service-card__overlay';
      overlay.innerHTML = '<span>View</span>';
      media.appendChild(overlay);
    });
  }

  /* ----------------------------------------------------------
   * Init
   * ---------------------------------------------------------- */
  function init() {
    initBrandedPreloader();
    if (!document.getElementById('kob-preload-screen')) {
      document
        .querySelectorAll('.slideshow')
        .forEach((s) => s.classList.add('kob-hero-ready'));
    }
    initHeroWordReveal();
    initScrollReveal();
    initSalonSliderCards();
    initInstagramGallery();

    document
      .querySelectorAll('.service-category-card')
      .forEach((card) => card.classList.add('kob-service-card-enhanced'));

    document.querySelectorAll('a.btn-primary, a.btn-outline').forEach((btn) => {
      if (/book/i.test(btn.textContent)) {
        btn.classList.add('kob-booking-btn');
      }
    });

    /* Desktop: GSAP effects ASAP. Mobile: after LCP so score can climb. */
    runAfterLcp(() => {
      whenGsapReady(() => {
        initHeroKenBurns();
        initParallax(document.getElementById('MainContent') || document);
        initInstagramParallax();
        initMilestoneTimeline();
        if (typeof ScrollTrigger !== 'undefined') {
          ScrollTrigger.refresh();
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('load', () => {
    if (isMobileViewport) return;
    whenGsapReady(() => {
      initHeroKenBurns();
      initParallax(document.getElementById('MainContent') || document);
      initInstagramParallax();
      initMilestoneTimeline();
      if (typeof ScrollTrigger !== 'undefined') {
        ScrollTrigger.refresh();
      }
    });
  });

  document.addEventListener('shopify:section:load', (event) => {
    runAfterLcp(() => {
      whenGsapReady(() => {
        initHeroKenBurns(event.target);
        initParallax(event.target);
        initInstagramParallax(event.target);
        if (event.target.querySelector?.('.kob-milestone-section')) {
          event.target
            .querySelectorAll('.kob-milestone-section')
            .forEach((s) => {
              s.dataset.kobMilestoneInit = '';
            });
          initMilestoneTimeline();
        }
        if (typeof ScrollTrigger !== 'undefined') {
          ScrollTrigger.refresh();
        }
      });
    });
  });
})();
